import { Box, Divider, Flex, Stack, Title } from '@mantine/core'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import React from 'react'
import ChartsCard from '../../../../components/charts/charts_card'
import CountryInput from '../../../../components/inputs/country_input'
import Layout from '../../../../components/layout'
import { searchAPI_v2 } from '../../../../utils/axios'
import prerenderImportantLocales from '../../../../utils/prerender_locales.js'

const getCurrentMonthAndYear = () => {
  const date = new Date()
  const month = date.toLocaleString('en', { month: 'long' })
  const year = date.getFullYear()
  return { month: month.charAt(0).toUpperCase() + month.slice(1), year }
}

export default function PlatformHitsPage({ platformData }) {
  const t = useTranslations('Top')
  const t_month = useTranslations('Month')
  const router = useRouter()
  const { month, year } = getCurrentMonthAndYear()
  const platformName = platformData?.platformDetails?.platform_names[0] || ''
  const currentCountry = router.query.country

  const handleCountryChange = (selectedCountry) => {
    if (selectedCountry) {
      const platform = router.query.platform
      router.push(`/hits/${selectedCountry}/${platform}`)
    }
  }

  console.log({ platformData })

  // Filtrar duplicados por uid antes de renderizar
  const uniqueContent = platformData.data.reduce((acc, current) => {
    if (!acc.find((item) => item.uid === current.uid)) {
      acc.push(current)
    }
    return acc
  }, [])

  return (
    <>
      <Head>
        <title>
          {t('tabTitle', {
            platformName: platformName,
            month: t_month(month),
            year
          })}
        </title>
      </Head>
      <Layout
        showSearchBar
        withAutocomplete
        showHero={true}
        pageName="Platform Hits"
        heroTitle={t('heroTitle', {
          platformName: platformName.toUpperCase(),
          month: t_month(month).toUpperCase(),
          year: year
        })}
        heroSubtitle={t('heroSubtitle', {
          platformName,
          month: t_month(month),
          year
        })}
        logo={platformData.platformDetails.logo_url}
        logoSpacing={10}
        titleMarginTop={0}
      >
        <Box
          sx={(theme) => ({
            width: '80vw',
            maxWidth: '1366px',
            margin: '0 auto',

            [theme.fn.smallerThan('sm')]: {
              width: '95vw'
            }
          })}
        >
          <Stack spacing="lg">
            <Stack spacing="xs">
              <Flex
                justify="space-between"
                align="center"
                w="100%"
                sx={(theme) => ({
                  marginTop: theme.fn.smallerThan('sm') ? '3rem' : '0'
                })}
              >
                <Title
                  size="1.125rem"
                  order={4}
                  sx={{
                    fontWeight: 'lighter'
                  }}
                >
                  {t('tab', {
                    platformName: platformName.toUpperCase(),
                    month: t_month(month).toUpperCase(),
                    year
                  })}
                </Title>

                <CountryInput
                  onChange={handleCountryChange}
                  value={currentCountry}
                  defaultValue={currentCountry}
                />
              </Flex>
              <Divider
                size="xs"
                sx={{
                  borderColor: '#7b39b6',
                  width: '2.5rem',
                  alignSelf: 'left',
                  marginBottom: '1rem'
                }}
              />
            </Stack>

            <Stack
              spacing="sm"
              sx={(theme) => ({
                maxWidth: '85rem',
                width: '100%',
                margin: '0 auto',
                [theme.fn.smallerThan('sm')]: {
                  padding: '0 1rem',
                  maxWidth: '95%'
                }
              })}
            >
              {uniqueContent.slice(0, 50).map((content, index) => (
                <React.Fragment key={content.uid || index}>
                  {index !== 0 && (
                    <Divider
                      sx={{
                        marginLeft: '3rem',
                        marginTop: '1rem',
                        marginBottom: '1rem'
                      }}
                    />
                  )}
                  <ChartsCard
                    data={{
                      imageUrl: content.poster,
                      position: index + 1,
                      previousPosition: content.previous_position_change || 0,
                      rank: index + 1,
                      badgeText: content.content_type || 'New',
                      badgeNumber: content.hits,
                      title: content.title,
                      genre: content.genre || 'N/A',
                      id: content.slug || '',
                      releaseDate: content.year || 'N/A',
                      primaryCountry: content.primary_country || 'N/A',
                      type: content.content_type.toLowerCase(),
                      availability: content.availability
                    }}
                    currentPlatform={platformName}
                    isTop50={true}
                    showBadgeNumber={true}
                    sx={(theme) => ({
                      maxWidth: theme.fn.smallerThan('sm') ? '95%' : '100%',
                      margin: '0 auto'
                    })}
                  />
                </React.Fragment>
              ))}
            </Stack>
          </Stack>
        </Box>
      </Layout>
    </>
  )
}

PlatformHitsPage.propTypes = {
  platformData: PropTypes.shape({
    platform: PropTypes.string,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        uid: PropTypes.string,
        title: PropTypes.string,
        poster: PropTypes.string,
        content_type: PropTypes.string,
        year: PropTypes.number,
        slug: PropTypes.string,
        primary_genre: PropTypes.string,
        previous_position_change: PropTypes.number,
        primary_country: PropTypes.string
      })
    )
  }),
  error: PropTypes.string
}

export async function getStaticProps({ locale, params }) {
  prerenderImportantLocales(locale)

  const countryCode = params.country.toLowerCase()
  const language = locale.split('-')[0]?.toLowerCase()

  const { platform } = params
  const messages = (
    await import(`../../../../locales/${locale.split('-')[0]}/common.json`)
  ).default

  const platformId = platform.split('-')[0]

  const fetchPlatformDetails = async (platformId) => {
    const { data: platformData } = await searchAPI_v2.get(`platforms/${platformId}`)
    return platformData
  }

  const fetchPlatformHits = async (params) => {
    const { data: moviesData } = await searchAPI_v2.get('content/hits/v3', {
      params: { ...params, type: 'movie' }
    })
    const { data: seriesData } = await searchAPI_v2.get('content/hits/v3', {
      params: { ...params, type: 'series' }
    })

    const parsedMovies = moviesData.data.map((movie) => ({
      title: movie.translation?.title || movie.title,
      poster: movie.translation?.poster || movie.poster,
      content_type: movie.content_type,
      genre: movie.genres[0] || '',
      year: movie.year,
      uid: movie.uid,
      slug: movie.slug,
      primary_genre: movie.genres?.[0] || 'N/A',
      previous_position_change: movie.previous_position_change || 0,
      primary_country: movie.primary_country || 'N/A',
      availability: movie.svod_availability?.filter(Boolean) || [],
      hits: movie.hits || 0
    }))

    const parsedSeries = seriesData.data.map((series) => ({
      title: series.translation?.title || series.title,
      poster: series.poster,
      content_type: series.content_type,
      genre: series.genres[0] || '',
      year: series.year,
      uid: series.uid,
      slug: series.slug,
      primary_genre: series.genres?.[0] || 'N/A',
      previous_position_change: series.previous_position_change || 0,
      primary_country: series.primary_country || 'N/A',
      availability: series.svod_availability?.filter(Boolean) || [],
      hits: series.hits || 0
    }))

    return [...parsedMovies, ...parsedSeries].sort((a, b) => b.hits - a.hits)
  }

  const platformParams = {
    country: countryCode,
    type: ['movie', 'series'],
    limit: 50,
    platform: platformId,
    language: language
  }

  try {
    const platformDetails = await fetchPlatformDetails(platformId)
    const data = await fetchPlatformHits(platformParams)

    return {
      props: {
        platformData: {
          platformDetails,
          data
        },
        messages
      },
      revalidate: 60 * 60 * 24
    }
  } catch (error) {
    console.error('Error fetching platform hits:', error)
    return {
      props: {
        error: 'No se pudieron cargar los datos de la plataforma.'
      },
      revalidate: 60 * 60 * 24
    }
  }
}

export async function getStaticPaths() {
  const countries = ['us', 'uk', 'fr', 'de', 'ar', 'es', 'mx', 'br', 'it']
  const platforms = ['34', '56']

  const paths = countries.flatMap((country) =>
    platforms.map((platform) => ({
      params: { country, platform }
    }))
  )

  return {
    paths,
    fallback: 'blocking'
  }
}
